const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

export const apiCall = async (endpoint, options = {}) => {
  const url = `${API_URL}${endpoint}`;
  const timeout = 15000;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  const config = {
    ...options,
    signal: controller.signal,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);
    clearTimeout(timeoutId);
    
    if (options.responseType === 'blob') {
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP Error: ${response.status}`);
      }
      return response.blob();
    }
    
    const contentType = response.headers.get('content-type');
    const isJson = contentType && contentType.includes('application/json');
    
    if (!response.ok) {
      if (isJson) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP Error: ${response.status}`);
      } else {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP Error: ${response.status}`);
      }
    }
    
    if (isJson) {
      const data = await response.json();
      
      if (data.data !== undefined) {
        return { 
          success: data.success !== false, 
          data: data.data,
          raw: data
        };
      } else {
        return { 
          success: data.success !== false, 
          data: data,
          raw: data
        };
      }
    } else {
      const text = await response.text();
      return { 
        success: true, 
        data: text,
        raw: text
      };
    }
    
  } catch (error) {
    clearTimeout(timeoutId);
    
    let errorMessage = error.message;
    if (error.name === 'AbortError') {
      errorMessage = 'Request timeout. Please check your network connection.';
    } else if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
      errorMessage = 'Network error. Please check if server is running.';
    }
    
    console.error('API Error:', {
      endpoint,
      error: errorMessage,
      url
    });
    
    return { 
      success: false, 
      error: errorMessage,
      data: null 
    };
  }
};

export const api = {
  // ==================== AUTH ====================
  login: (credentials) => apiCall('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify(credentials)
  }),

  register: (userData) => apiCall('/api/auth/register', {
    method: 'POST',
    body: JSON.stringify(userData)
  }),

  logout: () => apiCall('/api/auth/logout', { method: 'POST' }),

  getMe: () => apiCall('/api/auth/me'),

  // ==================== PROFILE ====================
  getProfile: () => apiCall('/api/profile'),

  updateProfile: (data) => apiCall('/api/profile', {
    method: 'PATCH',
    body: JSON.stringify(data)
  }),

  // ==================== BLOG ====================
  getBlogs: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/api/blog?${query}`);
  },

  getBlog: (id) => apiCall(`/api/blog/${id}`),

  createBlog: (blogData) => apiCall('/api/blog', {
    method: 'POST',
    body: JSON.stringify(blogData)
  }),

  updateBlog: (id, blogData) => apiCall(`/api/blog/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(blogData)
  }),

  deleteBlog: (id) => apiCall(`/api/blog/${id}`, { method: 'DELETE' }),

  // ==================== FEEDBACK ====================
  getFeedback: () => apiCall('/api/feedback'),

  submitFeedback: (feedbackData) => apiCall('/api/feedback', {
    method: 'POST',
    body: JSON.stringify(feedbackData)
  }),

  // ==================== FORUM ====================
  getForumMessages: () => apiCall('/api/forum/messages'),

  postForumMessage: (messageData) => apiCall('/api/forum/messages', {
    method: 'POST',
    body: JSON.stringify(messageData)
  }),

  // ==================== ANALYTICS ====================
  getAnalyticsStats: () => apiCall('/api/analytics/stats'),

  startSession: (sessionData) => apiCall('/api/analytics/session/start', {
    method: 'POST',
    body: JSON.stringify(sessionData)
  }),

  trackPageView: (pageData) => apiCall('/api/analytics/pageview', {
    method: 'POST',
    body: JSON.stringify(pageData)
  }),

  // ==================== ADMIN ====================
  getAdminOverview: () => apiCall('/api/admin/overview'),

  getUsers: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/api/users?${query}`);
  },

  updateUserStatus: (userId, status) => apiCall(`/api/users/${userId}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status })
  }),

  // ==================== CAREERS ====================
  getCareers: () => apiCall('/api/careers'),

  getJob: (id) => apiCall(`/api/careers/${id}`),

  applyForJob: (formData) => {
    const headers = getAuthHeaders();
    delete headers['Content-Type'];
    
    return fetch(`${API_URL}/api/applications`, {
      method: 'POST',
      headers: { 'Authorization': headers['Authorization'] },
      body: formData
    }).then(res => res.json());
  },

  // ==================== CONTACT ====================
  sendContact: (data) => apiCall('/api/contact', {
    method: 'POST',
    body: JSON.stringify(data)
  }),


  getChatThread: (threadId) => apiCall(`/api/chat/threads/${threadId}`),

  // ✅ NEW: Send message with deduplication
  sendChatMessage: (threadId, messageData) => {
    const clientMessageId = `client_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    return apiCall(`/api/chat/threads/${threadId}/messages`, {
      method: 'POST',
      body: JSON.stringify({
        ...messageData,
        clientMessageId
      })
    });
  },

  getGuestThreadsByEmail: (email, name) => {
    const params = new URLSearchParams({
      email: email || '',
      name: name || ''
    });
    return apiCall(`/api/chat/guest/threads?${params}`);
  },

  // ✅ NEW: Heartbeat for online presence
  sendHeartbeat: () => apiCall('/api/chat/heartbeat', {
    method: 'POST'
  }),

  // ✅ ADMIN REAL-TIME MESSAGES
  getChatLiveMessages: (threadId, lastMessageId) => {
    let url = `/api/chat/admin/threads/${threadId}/live-messages`;
    if (lastMessageId) {
      url += `?lastMessageId=${encodeURIComponent(lastMessageId)}`;
    }
    return apiCall(url);
  },

  // ✅ USER ONLINE STATUS CHECK
  getUserAdminStatus: () => apiCall('/api/chat/user/online-status'),

  // ✅ USER HEARTBEAT
  sendUserHeartbeat: () => apiCall('/api/chat/user/heartbeat', {
    method: 'POST'
  }),

  // In the api object, add these:

  // ✅ ADMIN HEARTBEAT
  sendChatHeartbeat: () => apiCall('/api/chat/admin/heartbeat', {
    method: 'POST'
  }),

  // ✅ GET NEW MESSAGES (Optimized)
  getChatNewMessages: (threadId, lastMessageId) => {
    let url = `/api/chat/threads/${threadId}/new-messages`;
    if (lastMessageId) {
      url += `?lastMessageId=${encodeURIComponent(lastMessageId)}`;
    }
    return apiCall(url);
  },

  // ✅ LONG POLL (Optional)
  longPollMessages: (threadId, lastMessageId) => {
    let url = `/api/chat/threads/${threadId}/long-poll`;
    if (lastMessageId) {
      url += `?lastMessageId=${encodeURIComponent(lastMessageId)}`;
    }
    return apiCall(url, { timeout: 35000 }); // 35 second timeout
  },


  
  // ==================== CREATE & GET THREADS ====================
  
  createChatThread: (threadData) => apiCall('/api/chat/threads', {
    method: 'POST',
    body: JSON.stringify(threadData)
  }),

  getChatThreads: () => apiCall('/api/chat/user/threads'),

  // ==================== ADMIN INBOX & MANAGEMENT ====================
  
  getChatAdminInbox: () => apiCall('/api/chat/admin/inbox'),

  updateChatThreadStatus: (threadId, status) => apiCall(`/api/chat/threads/${threadId}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status })
  }),

  searchChatThreads: (query) => apiCall(`/api/chat/admin/search?q=${encodeURIComponent(query)}`),

  getChatStats: () => apiCall('/api/chat/admin/stats'),

  // ==================== EXPORT & EMAIL ====================
  
  exportChatTranscript: (threadId) => apiCall(`/api/chat/threads/${threadId}/export`, {
    responseType: 'blob'
  }),

  emailChatTranscript: (threadId, data) => apiCall(`/api/chat/threads/${threadId}/email-transcript`, {
    method: 'POST',
    body: JSON.stringify(data)
  }),

  checkAdminOnline: () => apiCall('/api/chat/admin/online-status'),

  endChatThread: (threadId, data = {}) => apiCall(`/api/chat/threads/${threadId}/end`, {
    method: 'PATCH',
    body: JSON.stringify(data)
  }),

  // ==================== UTILITY ====================
  
  ping: () => apiCall('/api/ping')

};

export const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

export const handleExportTranscript = async (threadId, threadTitle = 'chat_transcript') => {
  try {
    const blob = await api.exportChatTranscript(threadId);
    
    if (blob instanceof Blob) {
      downloadBlob(blob, `${threadTitle}_${threadId}.txt`);
      return { success: true, message: 'Transcript downloaded successfully' };
    } else {
      throw new Error('Invalid response format');
    }
  } catch (error) {
    console.error('Export failed:', error);
    return { 
      success: false, 
      error: error.message || 'Failed to download transcript' 
    };
  }
};

export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return { success: true, message: 'Copied to clipboard' };
  } catch (error) {
    console.error('Copy failed:', error);
    return { success: false, error: 'Failed to copy to clipboard' };
  }
};

export default api;